// PTE Core full mock sitting: Part 1 Speaking & Writing, Part 2 Reading,
// Part 3 Listening, the real part order, forward-only, no review until the
// sitting ends. Speaking items record inline on the exam's clock; constructed
// responses submit to the W/S pipeline; auto-scorable sections grade
// server-side at part boundaries.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, Mic, Volume2 } from 'lucide-react';

import { api } from '@/lib/api';
import { gateFlagText, prettyTask } from '@/lib/labels';
import { Button } from '@/components/ui/button';
import DiagramCard from '@/components/DiagramCard';
import { FibDropdown, FibDrag, FibTyped, HiwWords, McqOptions, Reorder, ReviewBlock, buildResponses } from '@/components/PTEInteractions';
import { acquireMicStream, buildLevelMeter, releaseStream as releaseMicStream } from '@/lib/micRecorder';import { cn } from '@/lib/utils';

const PARTS = [
  { title: 'Part 1: Speaking & Writing', sections: ['speaking', 'writing'], time: 'about 50 minutes', clockSec: null },
  { title: 'Part 2: Reading', sections: ['reading'], time: '27-37 minutes', clockSec: 30 * 60 },
  { title: 'Part 3: Listening', sections: ['listening'], time: '22-37 minutes', clockSec: 25 * 60 },
];

const SPEAK_TITLES = {
  pte_read_aloud: 'Read Aloud', pte_repeat_sentence: 'Repeat Sentence',
  pte_describe_image: 'Describe Image', pte_respond_situation: 'Respond to a Situation',
  pte_asq: 'Answer Short Question',
};

const TYPED_LIMITS = { summarize_written_text: 10, write_email: 9, l_sst: 8 };

function playTone(onDone) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880; gain.gain.value = 0.15;
    osc.connect(gain); gain.connect(ctx.destination); osc.start();
    setTimeout(() => { osc.stop(); ctx.close(); onDone(); }, 350);
  } catch { onDone(); }
}

const wordsOf = (t) => (t || '').trim().split(/\s+/).filter(Boolean).length;

// Mic acquisition + live level metering lives in lib/micRecorder.js so a
// broken stream can never sneak through to the grader.

// ---- inline speaking item (records once on the exam clock) ------------------
// `sharedMic` is the { stream, mime } acquired at the Part 1 BEGIN click. Safari
// requires getUserMedia() to be called synchronously inside a user-gesture
// handler; calling it from useEffect (as we used to) is treated as a
// non-gesture request and silently rejected — the student would land on the
// "Microphone is blocked" screen without ever seeing a permission prompt.
// The parent runner now hands us an already-acquired stream so we never have
// to prompt from a mount effect.
function SpeakingItem({ set, onDone, sharedMic }) {
  const { form: mockForm } = useParams();
  const [stage, setStage] = useState('AUDIO_OR_PREP'); // MIC_PROMPT, AUDIO, PREP, RECORD, UPLOADING, FAILED
  const [countdown, setCountdown] = useState(0);
  const stageRef = useRef('AUDIO_OR_PREP');
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedRef = useRef(0);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const mimeRef = useRef({ mime: '', ext: 'webm' });
  const meterRef = useRef(null);
  const levelRafRef = useRef(0);
  const [level, setLevel] = useState(0);
  const spec = set.response_spec || {};
  const stim = set.stimulus || {};

  const setStageBoth = (s) => { setStage(s); stageRef.current = s; };

  const stopRecording = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  }, []);

  const releaseStream = useCallback(() => {
    if (meterRef.current) { meterRef.current.stop(); meterRef.current = null; }
    if (levelRafRef.current) { cancelAnimationFrame(levelRafRef.current); levelRafRef.current = 0; }
    const stream = streamRef.current;
    // Only stop the stream if we own it. When the parent handed us a shared
    // stream we keep it alive for the next item.
    if (stream && !sharedMic) {
      releaseMicStream(stream);
      streamRef.current = null;
    }
  }, [sharedMic]);

  const record = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) { setStageBoth('FAILED'); return; }
    try {
      const { mime, ext } = mimeRef.current;
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      startedRef.current = Date.now();
      meterRef.current = buildLevelMeter(stream);
      const pollLevel = () => {
        setLevel(meterRef.current?.getLevel() || 0);
        levelRafRef.current = requestAnimationFrame(pollLevel);
      };
      levelRafRef.current = requestAnimationFrame(pollLevel);
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const clientSilent = !(meterRef.current?.didSpeak());
        releaseStream();
        setStageBoth('UPLOADING');
        try {
          const form = new FormData();
          form.append('set_id', set.set_id);
          form.append('duration_sec', String((Date.now() - startedRef.current) / 1000));
          form.append('audio', new Blob(chunksRef.current, rec.mimeType ? { type: rec.mimeType } : {}), `response.${ext}`);
          if (mockForm) form.append('mock_form', String(mockForm));
          if (clientSilent) form.append('client_silent', 'true');
          const res = await api.submitSpeaking('pte', form);
          onDone({ kind: 'speaking', set, submission: res.data.submission });
        } catch {
          onDone({ kind: 'speaking', set, failed: true });
        }
      };
      rec.start();
      setStageBoth('RECORD');
      setCountdown(spec.record_seconds || 40);
    } catch {
      releaseStream();
      setStageBoth('FAILED');
    }
  }, [set, spec.record_seconds, onDone, mockForm, releaseStream]);

  const openMic = useCallback(() => {
    if (spec.tone) playTone(record);
    else record();
  }, [spec.tone, record]);

  const afterAudio = useCallback(() => {
    if ((spec.prep_seconds || 0) > 0) { setCountdown(spec.prep_seconds); setStageBoth('PREP'); }
    else openMic();
  }, [spec.prep_seconds, openMic]);

  // Full pre-flight: acquire mic, then play stim audio (if any), then run the
  // prep/record clock. Used at mount AND from the FAILED-stage "Try again"
  // button so a denied user can re-trigger the browser permission prompt
  // without leaving the mock sitting.
  const cancelledRef = useRef(false);
  const [micError, setMicError] = useState('');
  const startFlow = useCallback(async () => {
    cancelledRef.current = false;
    setMicError('');
    // Prefer the shared stream acquired at Part 1 BEGIN. Only fall back to
    // in-mount acquisition if the parent never handed one down (defensive:
    // this fallback will fail on Safari, but the error surfaces cleanly).
    const parentTrack = sharedMic?.stream?.getAudioTracks?.().find((t) => t.readyState === 'live' && t.enabled);
    if (sharedMic && parentTrack) {
      streamRef.current = sharedMic.stream;
      mimeRef.current = sharedMic.mime;
    } else {
      setStageBoth('MIC_PROMPT');
      try {
        const { stream, mime } = await acquireMicStream();
        if (cancelledRef.current) { releaseMicStream(stream); return; }
        streamRef.current = stream;
        mimeRef.current = mime;
      } catch (err) {
        if (!cancelledRef.current) {
          setMicError((err && err.message) || 'The browser could not open the microphone.');
          setStageBoth('FAILED');
        }
        return;
      }
    }
    if (stim.audio_ref) {
      setStageBoth('AUDIO');
      try {
        const res = await api.contentAudioBlob(stim.audio_ref);
        if (cancelledRef.current) return;
        const url = URL.createObjectURL(res.data);
        const a = new Audio(url);
        audioRef.current = a;
        // Same recovery net as PTEItemRunner: onerror + watchdog so a stall
        // during a MOCK sitting cannot trap the student on "Audio is playing"
        // for the entire section clock.
        let watchdog = null;
        const proceed = () => {
          if (watchdog) { clearTimeout(watchdog); watchdog = null; }
          URL.revokeObjectURL(url);
          if (stageRef.current === 'AUDIO') afterAudio();
        };
        a.onended = proceed;
        a.onerror = proceed;
        watchdog = setTimeout(() => {
          if (stageRef.current === 'AUDIO') {
            try { a.pause(); } catch { /* ignore */ }
            proceed();
          }
        }, 90_000);
        await a.play();
      } catch { afterAudio(); }
    } else {
      afterAudio();
    }
  }, [stim.audio_ref, afterAudio, sharedMic]);

  useEffect(() => {
    cancelledRef.current = false;
    startFlow();
    return () => {
      cancelledRef.current = true;
      if (audioRef.current) audioRef.current.pause();
      stopRecording();
      releaseStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set.set_id]);

  useEffect(() => {
    if (stage !== 'PREP' && stage !== 'RECORD') return undefined;
    const t = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) {
          clearInterval(t);
          if (stageRef.current === 'PREP') openMic();
          if (stageRef.current === 'RECORD') stopRecording();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [stage, openMic, stopRecording]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-blue-900">{SPEAK_TITLES[set.task_type] || 'Speaking'}</p>
        {(stage === 'PREP' || stage === 'RECORD') && (
          <span className={cn('rounded px-2.5 py-1 text-sm font-semibold tabular-nums text-white', stage === 'PREP' ? 'bg-blue-900' : 'bg-red-600')} data-testid="mock-speak-countdown">
            {stage === 'PREP' ? 'Beginning in: ' : 'Recording: '}{countdown}s
          </span>
        )}
      </div>
      {stage === 'MIC_PROMPT' && (
        <div className="flex items-center justify-center gap-3 border border-slate-300 bg-white px-6 py-14 text-sm font-semibold text-slate-600">
          <Mic className="h-5 w-5 animate-pulse text-blue-900" /> Asking your browser for microphone access. Click Allow to begin.
        </div>
      )}
      {stage === 'AUDIO' && (
        <div className="flex items-center justify-center gap-3 border border-slate-300 bg-white px-6 py-14 text-sm font-semibold text-slate-600">
          <Volume2 className="h-5 w-5 animate-pulse text-blue-900" /> Audio is playing. It plays only once.
        </div>
      )}
      {(stage === 'PREP' || stage === 'RECORD') && (
        <>
          {(stim.text || stim.diagram) && (
            <div className="border border-slate-300 bg-white px-6 py-5">
              {stim.diagram && <DiagramCard diagram={stim.diagram} />}
              {stim.text && <p className="text-[17px] leading-8 text-slate-800">{stim.text}</p>}
            </div>
          )}
          <div className={cn('flex items-center justify-center gap-3 border px-6 py-5 text-sm font-semibold',
            stage === 'PREP' ? 'border-slate-300 bg-white text-slate-600' : 'border-red-200 bg-red-50 text-red-700')}>
            <span className={cn('h-2.5 w-2.5 rounded-full', stage === 'PREP' ? 'bg-slate-400' : 'animate-pulse bg-red-600')} />
            {stage === 'PREP'
              ? (set.response_spec?.tone ? 'Prepare. A short tone plays when recording begins.' : 'Prepare. Recording begins when the countdown ends.')
              : 'Recording. Speak now.'}
          </div>
          {stage === 'RECORD' && (
            <div className="border border-slate-200 bg-white px-5 py-4" data-testid="mock-speak-mic-meter">
              <div className="flex items-center gap-3">
                <Mic className="h-4 w-4 text-slate-500" />
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mic input</p>
                <p className="ml-auto text-xs text-slate-400">
                  {level > 0.035 ? 'Picking up your voice' : 'No sound detected — speak up'}
                </p>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className={cn('h-full transition-[width] duration-75', level > 0.035 ? 'bg-emerald-500' : 'bg-slate-300')}
                  style={{ width: `${Math.min(100, Math.round(level * 800))}%` }}
                />
              </div>
            </div>
          )}
        </>
      )}
      {stage === 'UPLOADING' && <p className="py-10 text-center text-sm text-slate-500">Saving your response…</p>}
      {stage === 'FAILED' && (
        <div className="border border-red-200 bg-red-50/60 px-6 py-5 text-sm text-slate-700" data-testid="mock-speak-failed">
          <p className="font-semibold text-red-700">We couldn&apos;t start the microphone.</p>
          <p className="mt-1 text-xs text-slate-600" data-testid="mock-speak-mic-error">
            {micError || 'Click Try again to ask your browser for permission. If no prompt appears, click the lock or camera icon in the address bar, allow the microphone for this site, then click Try again.'}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              data-testid="mock-speak-mic-retry"
              onClick={startFlow}
              className="inline-flex items-center rounded bg-blue-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800"
            >
              Try again
            </button>
            <Button size="sm" variant="outline" onClick={() => onDone({ kind: 'speaking', set, skipped: true })}>
              Skip item
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- typed constructed item (SWT / Write Email / SST) -----------------------
function TypedItem({ set, onDone }) {
  const { form: mockForm } = useParams();
  const spec = set.response_spec || {};
  const stim = set.stimulus || {};
  const limitMin = spec.time_limit_minutes || TYPED_LIMITS[set.task_type] || 10;
  const [stage, setStage] = useState(stim.audio_ref ? 'AUDIO' : 'WRITE');
  const [text, setText] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(limitMin * 60);
  const [submitting, setSubmitting] = useState(false);
  const audioRef = useRef(null);
  const submittedRef = useRef(false);

  const submit = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const res = await api.submitWriting('pte', { set_id: set.set_id, text: text || '(no response)', seconds_used: limitMin * 60 - secondsLeft, mock_form: Number(mockForm) });
      onDone({ kind: 'typed', set, submission: res.data.submission });
    } catch {
      onDone({ kind: 'typed', set, failed: true });
    }
  }, [set, text, secondsLeft, limitMin, onDone]);

  useEffect(() => {
    let cancelled = false;
    if (stage !== 'AUDIO') return undefined;
    let localAudio = null;
    let watchdog = null;
    (async () => {
      try {
        const res = await api.contentAudioBlob(stim.audio_ref);
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        const a = new Audio(url);
        audioRef.current = a;
        localAudio = a;
        const proceed = () => {
          if (cancelled) return;
          if (watchdog) { clearTimeout(watchdog); watchdog = null; }
          URL.revokeObjectURL(url);
          setStage('WRITE');
        };
        a.onended = proceed;
        a.onerror = proceed;
        watchdog = setTimeout(() => {
          if (!cancelled && stage === 'AUDIO') {
            try { a.pause(); } catch { /* ignore */ }
            proceed();
          }
        }, 90_000);
        await a.play();
      } catch { if (!cancelled) setStage('WRITE'); }
    })();
    return () => {
      cancelled = true;
      if (watchdog) clearTimeout(watchdog);
      if (localAudio) { try { localAudio.pause(); } catch { /* ignore */ } }
      if (audioRef.current) audioRef.current.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  useEffect(() => {
    if (stage !== 'WRITE') return undefined;
    if (secondsLeft <= 0) { submit(); return undefined; }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [stage, secondsLeft, submit]);

  const wc = wordsOf(text);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-blue-900">
          {set.task_type === 'l_sst' ? 'Summarize Spoken Text' : set.task_type === 'write_email' ? 'Write Email' : 'Summarize Written Text'}
        </p>
        {stage === 'WRITE' && (
          <span className="rounded bg-blue-900 px-2.5 py-1 text-sm font-semibold tabular-nums text-white" data-testid="mock-typed-timer">
            {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
          </span>
        )}
      </div>
      {stage === 'AUDIO' && (
        <div className="flex items-center justify-center gap-3 border border-slate-300 bg-white px-6 py-14 text-sm font-semibold text-slate-600">
          <Volume2 className="h-5 w-5 animate-pulse text-blue-900" /> Audio is playing. It plays only once.
        </div>
      )}
      {stage === 'WRITE' && (
        <>
          {(spec.prompt_text || stim.text) && (
            <div className="border border-slate-300 bg-white px-6 py-5">
              <p className="text-[15px] leading-8 text-slate-800">{spec.prompt_text || stim.text}</p>
              {(spec.bullets || []).length > 0 && (
                <ul className="mt-3 list-disc space-y-1 pl-6 text-[15px] text-slate-700">
                  {spec.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )}
            </div>
          )}
          {spec.instruction && <p className="text-sm text-slate-500">{spec.instruction}</p>}
          <textarea
            className="h-48 w-full resize-none border border-slate-300 p-4 text-[15px] leading-7 text-slate-900 focus:border-blue-900 focus:outline-none"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="mock-typed-text"
          />
          <div className="flex items-center justify-between">
            <p className="text-sm tabular-nums text-slate-500">Total Word Count: {wc}</p>
            <Button className="bg-blue-900 px-8 hover:bg-blue-800" disabled={submitting} onClick={submit} data-testid="mock-typed-next">
              {submitting ? 'Saving…' : 'NEXT'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ---- auto-scorable item (reading + listening) --------------------------------
function ScoredItem({ set, onDone }) {
  const stim = set.stimulus || {};
  const isListening = set.section === 'listening';
  const [stage, setStage] = useState(isListening && stim.audio_ref ? 'COUNTDOWN' : 'RUN');
  const [preCount, setPreCount] = useState(3);
  const [responses, setResponses] = useState({});
  const audioRef = useRef(null);
  const setResp = (qid, payload) => setResponses((r) => ({ ...r, [qid]: { ...(r[qid] || {}), ...payload } }));
  const tt = set.task_type;

  useEffect(() => {
    if (stage !== 'COUNTDOWN') return undefined;
    if (preCount <= 0) {
      let cancelled = false;
      let watchdog = null;
      (async () => {
        try {
          const res = await api.contentAudioBlob(stim.audio_ref);
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          const a = new Audio(url);
          audioRef.current = a;
          // HIW + FIB show their interaction while the audio plays
          if (tt === 'l_hiw' || tt === 'l_fib') setStage('RUN_AUDIO');
          else setStage('AUDIO');
          const proceed = () => {
            if (cancelled) return;
            if (watchdog) { clearTimeout(watchdog); watchdog = null; }
            URL.revokeObjectURL(url);
            setStage('RUN');
          };
          a.onended = proceed;
          a.onerror = proceed;
          // Watchdog so a stalled clip cannot pin an entire mock sitting.
          watchdog = setTimeout(() => {
            if (!cancelled) {
              try { a.pause(); } catch { /* ignore */ }
              proceed();
            }
          }, 90_000);
          await a.play();
        } catch { if (!cancelled) setStage('RUN'); }
      })();
      return () => { cancelled = true; if (watchdog) clearTimeout(watchdog); };
    }
    const t = setTimeout(() => setPreCount((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [stage, preCount, stim.audio_ref, tt]);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const interactive = stage === 'RUN' || stage === 'RUN_AUDIO';
  return (
    <div className="space-y-6">
      {stage === 'COUNTDOWN' && (
        <div className="flex items-center justify-center gap-3 border border-slate-300 bg-white px-6 py-14 text-sm font-semibold text-slate-600" data-testid="mock-audio-countdown">
          <Volume2 className="h-5 w-5 text-blue-900" /> Audio begins in {preCount}…
        </div>
      )}
      {stage === 'AUDIO' && (
        <div className="flex items-center justify-center gap-3 border border-slate-300 bg-white px-6 py-14 text-sm font-semibold text-slate-600">
          <Volume2 className="h-5 w-5 animate-pulse text-blue-900" /> Audio is playing. It plays only once.
        </div>
      )}
      {interactive && (
        <>
          {stim.text && !['rw_fib', 'r_fib'].includes(tt) && (
            <div className="border border-slate-300 bg-white px-6 py-5">
              <p className="text-[15px] leading-8 text-slate-800">{stim.text}</p>
            </div>
          )}
          <div className="border border-slate-300 bg-white px-6 py-6">
            {tt === 'rw_fib' && <FibDropdown set={set} responses={responses} setResp={setResp} />}
            {tt === 'r_fib' && <FibDrag set={set} responses={responses} setResp={setResp} />}
            {tt === 'r_reorder' && <Reorder set={set} responses={responses} setResp={setResp} />}
            {tt === 'l_fib' && <FibTyped set={set} responses={responses} setResp={setResp} />}
            {tt === 'l_hiw' && <HiwWords set={set} responses={responses} setResp={setResp} />}
            {tt === 'l_wfd' && set.questions[0] && (
              <input
                type="text"
                className="w-full border border-slate-300 px-4 py-3 text-[15px] text-slate-900 focus:border-blue-900 focus:outline-none"
                placeholder="Type the sentence you heard."
                value={responses[set.questions[0].qid]?.text || ''}
                onChange={(e) => setResp(set.questions[0].qid, { text: e.target.value })}
                autoComplete="off" spellCheck="false"
              />
            )}
            {['r_mcq_multi', 'r_mcq_single', 'l_mcq_multi', 'l_mcq_single', 'l_hcs', 'l_smw'].includes(tt) &&
              set.questions.map((q) => (
                <div key={q.qid}>
                  {q.prompt && <p className="mb-4 text-[15px] font-medium text-slate-900">{q.prompt}</p>}
                  <McqOptions q={q} multi={tt.endsWith('mcq_multi')} responses={responses} setResp={setResp} />
                </div>
              ))}
          </div>
          <div className="flex justify-end">
            <Button className="bg-blue-900 px-10 hover:bg-blue-800" data-testid="mock-item-next"
              onClick={() => onDone({ kind: 'scored', set, responses: buildResponses(set, responses) })}>
              NEXT
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ---- the sitting --------------------------------------------------------------
export default function PTEMockRunner() {
  const { form } = useParams();
  const [sections, setSections] = useState(null);
  const [error, setError] = useState('');
  const [partIdx, setPartIdx] = useState(0);
  const [phase, setPhase] = useState('OVERVIEW'); // OVERVIEW, PART_INTRO, ITEM, GRADING, REPORT
  const [itemIdx, setItemIdx] = useState(0);
  const [partClock, setPartClock] = useState(null);
  const answersRef = useRef({}); // section -> {set_id: responses[]}
  const wsRef = useRef([]); // typed + speaking outcomes
  const [report, setReport] = useState(null);

  // The Part 1 mic stream is acquired synchronously inside the BEGIN button
  // click handler and reused across every speaking item. Safari refuses
  // getUserMedia() unless it is called from a user-gesture stack; the prior
  // per-item acquisition (from useEffect on mount) silently failed and put
  // the student straight on the "microphone is blocked" screen with no
  // permission prompt.
  const sharedMicRef = useRef(null); // { stream, mime } | null
  const [micError, setMicError] = useState('');
  const [micAcquiring, setMicAcquiring] = useState(false);
  const releaseSharedMic = useCallback(() => {
    const cur = sharedMicRef.current;
    if (cur?.stream) releaseMicStream(cur.stream);
    sharedMicRef.current = null;
  }, []);
  // Release the mic when the runner unmounts (leaves the mock entirely).
  useEffect(() => () => releaseSharedMic(), [releaseSharedMic]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.mockForm('pte', Number(form));
        setSections(res.data.sections);
      } catch (err) {
        setError(err.response?.data?.detail || 'This mock form is not available yet.');
      }
    })();
  }, [form]);

  const partItems = useCallback((pi) => {
    if (!sections) return [];
    return PARTS[pi].sections.flatMap((s) => sections[s] || []);
  }, [sections]);

  const finishSitting = useCallback(async () => {
    setPhase('GRADING');
    const out = { sections: [], ws: wsRef.current };
    for (const sectionName of ['reading', 'listening']) {
      const answers = answersRef.current[sectionName];
      if (!answers || !Object.keys(answers).length) continue;
      try {
        const res = await api.mockSectionSubmit('pte', { form: Number(form), section: sectionName, answers });
        out.sections.push(res.data);
      } catch { /* section had nothing gradable */ }
    }
    setReport(out);
    setPhase('REPORT');
  }, [form]);

  const advance = useCallback((result) => {
    if (result) {
      if (result.kind === 'scored') {
        const sec = result.set.section;
        answersRef.current[sec] = { ...(answersRef.current[sec] || {}), [result.set.set_id]: result.responses };
      } else {
        wsRef.current = [...wsRef.current, result];
      }
    }
    const items = partItems(partIdx);
    if (itemIdx + 1 < items.length) {
      setItemIdx(itemIdx + 1);
    } else if (partIdx + 1 < PARTS.length) {
      // Part 1 (Speaking & Writing) is finishing — free the mic so the OS
      // recording indicator turns off during Reading and Listening.
      if (partIdx === 0) releaseSharedMic();
      setPartIdx(partIdx + 1);
      setItemIdx(0);
      setPartClock(null);
      setPhase('PART_INTRO');
    } else {
      releaseSharedMic();
      finishSitting();
    }
  }, [partIdx, itemIdx, partItems, finishSitting, releaseSharedMic]);

  // BEGIN handler for a Part_INTRO. For Part 1 we call getUserMedia inside
  // this click handler (no awaits before it) so Safari treats it as a user
  // gesture and shows the permission prompt. For other parts it's a plain
  // phase transition.
  const beginPart = useCallback(async () => {
    const p = PARTS[partIdx];
    const needsMic = p.sections.includes('speaking');
    if (needsMic && !sharedMicRef.current) {
      setMicError('');
      setMicAcquiring(true);
      try {
        const acquired = await acquireMicStream();
        sharedMicRef.current = acquired;
      } catch (err) {
        setMicError((err && err.message) || 'The browser could not open the microphone.');
        setMicAcquiring(false);
        return;
      }
      setMicAcquiring(false);
    }
    setPhase('ITEM');
    if (p.clockSec) setPartClock(p.clockSec);
  }, [partIdx]);

  // part section clock (reading/listening)
  useEffect(() => {
    if (phase !== 'ITEM' || partClock == null) return undefined;
    if (partClock <= 0) {
      // time over: drop remaining items, move on with what is answered
      if (partIdx + 1 < PARTS.length) { setPartIdx(partIdx + 1); setItemIdx(0); setPartClock(null); setPhase('PART_INTRO'); }
      else finishSitting();
      return undefined;
    }
    const t = setTimeout(() => setPartClock((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, partClock, partIdx, finishSitting]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-100">
        <p className="max-w-md text-center text-slate-600">{error}</p>
        <Link to="/pte/mocks" className="text-sm font-medium text-blue-800 hover:underline">Back to mock tests</Link>
      </div>
    );
  }
  if (!sections) return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500">Assembling your sitting…</div>;

  const part = PARTS[partIdx];
  const items = partItems(partIdx);
  const current = items[itemIdx];

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-blue-900 text-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Mic className="h-4 w-4 opacity-80" />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-blue-200">PTE CORE mock · Form {form}</p>
              <p className="text-sm font-semibold">{phase === 'REPORT' ? 'Score report' : phase === 'OVERVIEW' ? 'Test overview' : part.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {phase === 'ITEM' && <span className="text-sm tabular-nums text-blue-200">{itemIdx + 1} of {items.length}</span>}
            {phase === 'ITEM' && partClock != null && (
              <span className="rounded bg-blue-950/60 px-2.5 py-1 text-sm font-semibold tabular-nums" data-testid="mock-part-clock">
                {Math.floor(partClock / 60)}:{String(partClock % 60).padStart(2, '0')}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        {phase === 'OVERVIEW' && (
          <div className="border border-slate-300 bg-white p-10 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Practice mock · full sitting</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-900">Before you begin</h1>
            <p className="mt-2 leading-7 text-slate-600">
              PTE Core is one continuous sitting of three parts, taken in order, in about two hours. Tasks
              appear one at a time and you cannot go back. No answers or feedback show until the end.
            </p>
            <ul className="mt-7 divide-y divide-slate-100 border-y border-slate-100">
              {PARTS.map((p, i) => (
                <li key={p.title} className="flex items-center gap-4 py-3.5" data-testid={`overview-part-${i}`}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">{p.title}</p>
                    <p className="text-xs text-slate-500">{partItems(i).length} tasks{p.clockSec ? ' · section clock' : ''}</p>
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-slate-400">{p.time}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800">
              Speaking and writing are recorded or typed and graded afterward; the reading and listening figures
              shown at the end are practice estimates from your accuracy, not official Pearson scores.
            </div>
            <div className="mt-8 flex items-center justify-between">
              <Link to="/pte/mocks" className="text-sm text-slate-500 hover:text-slate-800">Exit</Link>
              <Button size="lg" className="bg-blue-900 px-10 hover:bg-blue-800" data-testid="mock-overview-begin"
                onClick={() => setPhase('PART_INTRO')}>
                BEGIN TEST
              </Button>
            </div>
          </div>
        )}

        {phase === 'PART_INTRO' && (
          <div className="border border-slate-300 bg-white p-10 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-900">{part.time}</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-900">{part.title}</h1>
            <ul className="mt-6 space-y-3 text-[15px] leading-6 text-slate-700">
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />{items.length} tasks, presented one at a time. You cannot go back to a previous task.</li>
              {part.clockSec && <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />A section clock runs for this part. When it reaches zero, the part ends.</li>}
              {partIdx === 0 && <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />Speaking items record automatically on the exam's clock. Check your microphone now.</li>}
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />No answers or feedback appear until the whole sitting ends, exactly like test day.</li>
            </ul>
            <div className="mt-10 flex items-center justify-between">
              <Link to="/pte/mocks" className="text-sm text-slate-500 hover:text-slate-800">Exit sitting</Link>
              <Button size="lg" className="bg-blue-900 px-10 hover:bg-blue-800" data-testid="mock-part-start"
                disabled={micAcquiring}
                onClick={beginPart}>
                {micAcquiring ? 'Preparing microphone…' : 'BEGIN'}
              </Button>
            </div>
            {partIdx === 0 && micError && (
              <div className="mt-4 border border-red-200 bg-red-50/60 px-4 py-3 text-sm text-slate-700" data-testid="mock-part-mic-error">
                <p className="font-semibold text-red-700">We couldn&apos;t start the microphone.</p>
                <p className="mt-1 text-xs text-slate-600">{micError}</p>
                <p className="mt-1 text-xs text-slate-500">
                  In Safari: click Safari &gt; Settings &gt; Websites &gt; Microphone, set this site to Allow, then click BEGIN again.
                  In Chrome/Edge: click the lock icon in the address bar, allow the microphone for this site, then click BEGIN again.
                </p>
              </div>
            )}
          </div>
        )}

        {phase === 'ITEM' && current && (
          current.section === 'speaking'
            ? <SpeakingItem key={current.set_id} set={current} onDone={advance} sharedMic={sharedMicRef.current} />
            : (current.response_spec?.kind === 'typed' || current.section === 'writing')
              ? <TypedItem key={current.set_id} set={current} onDone={advance} />
              : <ScoredItem key={current.set_id} set={current} onDone={advance} />
        )}

        {phase === 'GRADING' && (
          <div className="flex flex-col items-center gap-3 py-24 text-slate-500">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-blue-900" /> Grading your sitting…
          </div>
        )}

        {phase === 'REPORT' && report && (
          <div className="space-y-8">
            <div className="border border-slate-300 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <h1 className="text-xl font-semibold text-slate-900">Sitting complete.</h1>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {report.sections.map((s) => (
                  <div key={s.section} className="border border-slate-200 px-5 py-4">
                    <p className="mono-stat capitalize">{s.section}</p>
                    <p className="h-display mt-1 text-3xl" data-testid={`mock-report-${s.section}`}>{s.n_correct} / {s.n_total}</p>
                    <p className="mt-1 text-sm text-slate-500">{Math.round((s.n_correct / Math.max(1, s.n_total)) * 100)}% of available points</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-sm leading-6 text-slate-500">
                These are practice points from this platform's graders. Official PTE Core scores (10-90 per skill)
                come from Pearson's scoring engine; your speaking and writing responses below will appear under
                Results once they’re marked.
              </p>
            </div>

            {report.ws.length > 0 && (
              <div className="border border-slate-300 bg-white p-8 shadow-sm">
                <p className="mono-stat">speaking + writing responses</p>
                <ul className="mt-4 divide-y divide-slate-100">
                  {report.ws.map((w, i) => (
                    <li key={i} className="flex items-center justify-between py-3 text-sm">
                      <span className="text-slate-700">{SPEAK_TITLES[w.set.task_type] || prettyTask(w.set.task_type)}</span>
                      <span className={cn('font-medium', w.skipped || w.failed ? 'text-red-600' : 'text-slate-500')}>
                        {w.skipped ? 'skipped' : w.failed ? 'not saved' : w.submission?.status === 'gate_zeroed' ? 'not counted (outside word limit)' : w.submission?.status === 'scored' ? 'scored' : 'submitted, being scored'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {report.sections.map((s) => {
              const bySet = {};
              s.results.forEach((r) => { (bySet[r.set_id] = bySet[r.set_id] || []).push(r); });
              return (
                <div key={s.section}>
                  <h2 className="h-display mb-4 text-xl capitalize">{s.section} review</h2>
                  <div className="space-y-6">
                    {Object.entries(bySet).map(([setId, rows]) => {
                      const original = (sections[s.section] || []).find((x) => x.set_id === setId);
                      return original ? <ReviewBlock key={setId} set={original} results={rows} /> : null;
                    })}
                  </div>
                </div>
              );
            })}

            <div className="flex justify-end">
              <Link to="/pte/mocks" className="cta">Back to mock tests</Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
